/**
 * Demo-mode fixtures: the served config, the token exchange, the host-link
 * status document and a training scenario, in the deployed wire shapes
 * (quest-client/functions/api/*.js and the state machine's scenario JSON).
 * Used by `?demo=` runs and the documentation screenshot harness - never by a
 * production build path.
 */

export type DemoMode = 'live' | 'connecting' | 'waiting' | 'blocked';

export function readDemoMode(search = typeof window !== 'undefined' ? window.location.search : ''): DemoMode | null {
  const value = new URLSearchParams(search).get('demo');
  if (value === null) return null;
  if (value === 'connecting' || value === 'waiting' || value === 'blocked') return value;
  return 'live';
}

/** `?autoplay=1` makes the demo room press the wearer's options itself, so a run walks the flow unattended (screenshots). */
export function readDemoAutoplay(search = typeof window !== 'undefined' ? window.location.search : ''): boolean {
  return new URLSearchParams(search).get('autoplay') === '1';
}

/** `?camera=none` makes demo mode skip the synthetic camera, to show the pre-flight failure. */
export function readDemoCameraDisabled(search = typeof window !== 'undefined' ? window.location.search : ''): boolean {
  return new URLSearchParams(search).get('camera') === 'none';
}

const inTwoHours = (): string => new Date(Date.now() + 2 * 3600 * 1000).toISOString();

export const DEMO_CONFIG_URL = 'https://demo.qvs.invalid/api/config';

export function demoConfig(): Record<string, unknown> {
  return {
    version: 2,
    server: '',
    livekitUrl: 'wss://demo.livekit.invalid',
    room: 'qvs-main',
    deployment: 'demo',
    assist: { autoAllow: true },
    wearerToken: 'demo-gate',
    assistCode: '482913',
    assistCodeExpiresAt: inTwoHours(),
  };
}

export function demoToken(): Record<string, unknown> {
  return {
    token: 'demo.jwt.token',
    identity: 'wearer',
    livekitUrl: 'wss://demo.livekit.invalid',
    room: 'qvs-main',
    deployment: 'demo',
    expiresAt: new Date(Date.now() + 12 * 3600 * 1000).toISOString(),
    claim: null,
    claimCheck: { ok: true, reason: null },
    wake: { delivered: true, reason: 'wearer-token' },
  };
}

const sessionStart = (): string => new Date(Date.now() - 12 * 60 * 1000).toISOString();

export function demoInUse(): Record<string, unknown> {
  return {
    error: 'in_use',
    message: 'someone is already training on this cell',
    since: sessionStart(),
    device: 'Quest 3 (bay 2)',
    cell: 'main',
    kind: 'wearer',
  };
}

export function demoHostStatus(status: 'in_use' | 'online'): Record<string, unknown> {
  return {
    v: 1,
    cell: 'main',
    status,
    detail: status === 'in_use' ? 'A training is running.' : 'Available for training.',
    reason: '',
    since: status === 'in_use' ? sessionStart() : null,
    host: { state: status === 'in_use' ? 'active' : 'dormant', training: 'EtharTrainingDemo', detector: 'yolo' },
    claim: status === 'in_use' ? { kind: 'wearer', device: 'Quest 3 (bay 2)', grantedAt: sessionStart() } : null,
    pendingActivation: null,
    fetchedAt: new Date().toISOString(),
  };
}

/** The demo scenario - the first steps of the repo's Ethar Training Demo scenario file. */
export const DEMO_SCENARIO = {
  name: 'Ethar Training Demo',
  steps: [
    {
      waitingClass: '',
      waitingState: '',
      detectedClass: '',
      label: '',
      title: 'Welcome',
      description: 'Ready to begin your Ethar training?',
      options: ['Begin'],
      imageRef: '',
      modelRef: '',
      result: 'training',
      resultState: 'begun',
    },
    {
      waitingClass: 'training',
      waitingState: 'begun',
      detectedClass: '',
      label: '',
      title: 'Look for a Monitor',
      description: 'Search your space and locate the monitor',
      options: ['Search'],
      imageRef: '',
      modelRef: '',
      result: 'tv',
      resultState: '',
    },
    {
      waitingClass: 'tv',
      waitingState: '',
      detectedClass: 'tv',
      label: 'This is a tv',
      title: 'TV Found',
      description: 'You found the TV - can you turn it on?',
      options: ["It's On", 'Not this one'],
      imageRef: '',
      modelRef: '',
      result: '',
      resultState: 'on',
    },
    {
      waitingClass: 'tv',
      waitingState: 'on',
      detectedClass: 'tv',
      label: 'This tv is on',
      title: 'TV Is On',
      description: 'Good - the TV is on. Training complete.',
      options: ['Finish'],
      imageRef: '',
      modelRef: '',
      result: '',
      resultState: '',
    },
  ],
};

/**
 * A `fetch` that answers the Functions the room service calls, per mode:
 * live (200 mint), connecting (the token call never answers), waiting (409
 * in_use + an in-use status document), blocked (503 not_configured).
 */
export function demoFetch(mode: DemoMode): typeof fetch {
  const json = (status: number, body: unknown): Response =>
    new Response(JSON.stringify(body), { status, headers: { 'content-type': 'application/json' } });

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = new URL(String(input));
    // The room service's own timeout fires first; this rejection only lands
    // if a caller ever awaits past it.
    const hang = (): Promise<Response> => new Promise(() => undefined);

    if (url.pathname === '/api/config') return json(200, demoConfig());
    if (url.pathname === '/api/token' && init?.method === 'POST') {
      switch (mode) {
        case 'connecting':
          return hang();
        case 'waiting':
          return json(409, demoInUse());
        case 'blocked':
          return json(503, { error: 'not_configured', message: 'wearer token gate is not configured' });
        default:
          return json(200, demoToken());
      }
    }
    if (url.pathname === '/api/host/status') return json(200, demoHostStatus(mode === 'waiting' ? 'in_use' : 'online'));
    return json(404, { error: 'not_found', message: `no demo route for ${url.pathname}` });
  };
}
